import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { useUpdatePaymentMutation } from 'api/main';
import {
    parsePaymentNumber,
    parseSum,
    jsonParseSafe,
    parseFullName,
    getPartialPaymentsTotal,
} from 'utils/helpers';
import { paymentStatus } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { MenuSendMessage, MenuSendSMS } from 'components/ActionsMenu/actions';
import { SettleMenuItem } from 'containers/payments/direct-debits/DebitsTable';
import {
    ActionButton,
    ActionsMenu,
    CancelButton,
    CustomDatePicker,
    CustomDialogTitle,
    Loader,
    StyledTableCell,
    StyledTableRow,
} from 'components';
import { Dialog, DialogActions, DialogContent, ListItemText, MenuItem } from '@mui/material';
import * as icons from 'mdi-material-ui';

const CreditMenuItem = ({ loading, ...rest }) => (
    <MenuItem
        disabled={loading}
        sx={{
            color: 'grey.700',
            '&:hover': { backgroundColor: 'primary.main', color: 'common.white' },
        }}
        {...rest}
    >
        <ListItemText disableTypography sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Loader loading={loading} marginTop={0}>
                <icons.CashPlus sx={{ fontSize: 25, color: 'inherit' }} /> {texts.CREDIT_PAYMENT}
            </Loader>
        </ListItemText>
    </MenuItem>
);

CreditMenuItem.propTypes = {
    loading: PropTypes.bool,
};

const SendMessageItem = React.forwardRef((pr, ref) => <MenuSendMessage ref={ref} {...pr} />);
const SettleItem = React.forwardRef((pr, ref) => <SettleMenuItem ref={ref} {...pr} />);
const CreditItem = React.forwardRef((pr, ref) => <CreditMenuItem ref={ref} {...pr} />);

const today = moment().startOf('day');

const TransferRow = props => {
    const { transfer, hasActions, isServicer, onSendSMS, onCredit } = props;
    const {
        recipientId,
        recipientName,
        closingComment,
        0: {
            id,
            fluxType,
            investedAmount,
            amount,
            status,
            validatedBy,
            transferDate,
            fundraisingNumber,
            subscription: {
                finishedSubscriptionAt,
                investmentCharacteristics: {
                    shareSubcategory: { immediatePayment },
                },
                investmentFund: { name: fundName, type: fundType, firstInstalmentPercentage },
            },
        },
    } = transfer;
    const user = { id: recipientId, fullName: recipientName };
    const [ongoingAction, setOngoingAction] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [updatePayment, { isSuccess: updateSuccess }] = useUpdatePaymentMutation();
    const isConfirmed = useMemo(() => status === paymentStatus.CONFIRMED, [status]);
    const partialPayments = useMemo(
        () => jsonParseSafe(transfer[0].partialPayments, []),
        [transfer]
    );
    const partialsTotal = useMemo(() => getPartialPaymentsTotal(transfer[0]), [transfer]);
    const paymentTransferDate = transferDate ? moment.utc(transferDate) : null;
    const isUrgent = useMemo(
        () => today.isSameOrAfter(paymentTransferDate, 'day'),
        [paymentTransferDate]
    );
    const isCritical = useMemo(
        () =>
            paymentTransferDate
                ? today.isAfter(moment(paymentTransferDate).add(2, 'day'), 'day')
                : false,
        [paymentTransferDate]
    );
    const rowStyle = useMemo(() => {
        if (isCritical) return { color: 'error.main' };
        if (isUrgent) return { color: 'warning.main' };
        return {};
    }, [isCritical, isUrgent]);
    const paymentNumber = firstInstalmentPercentage ? fundraisingNumber + 1 : fundraisingNumber;
    const paymentNumberText =
        firstInstalmentPercentage === 100 && immediatePayment
            ? texts.FULL_CALL
            : parsePaymentNumber(fluxType, paymentNumber, closingComment);
    const [settleDate, setSettleDate] = useState(paymentTransferDate);

    const handleDateChange = useCallback(newDate => setSettleDate(newDate), []);

    const handleUpdateStatus = useCallback(
        (paymentId, newStatus) => () => {
            setOngoingAction(newStatus);
            updatePayment({
                id: paymentId,
                status: newStatus,
                transferDate:
                    newStatus === paymentStatus.CONFIRMED ? settleDate.toISOString() : undefined,
            });
        },
        [updatePayment, settleDate]
    );

    useEffect(() => {
        if (updateSuccess) {
            setMenuOpen(false);
            setOngoingAction('');
        }
    }, [updateSuccess]);

    const handleCredit = useCallback(() => {
        onCredit(transfer[0]);
        setMenuOpen(false);
    }, [transfer, onCredit]);

    return (
        <StyledTableRow key={id} sx={hasActions ? rowStyle : {}}>
            <StyledTableCell>{recipientName}</StyledTableCell>
            <StyledTableCell>{moment(finishedSubscriptionAt).format('L')}</StyledTableCell>
            <StyledTableCell>{fundType}</StyledTableCell>
            <StyledTableCell>{fundName}</StyledTableCell>
            <StyledTableCell>{paymentNumberText}</StyledTableCell>
            <StyledTableCell>{parseSum(investedAmount)}</StyledTableCell>
            <StyledTableCell>{parseSum(amount)}</StyledTableCell>
            <StyledTableCell>
                {!partialPayments.length && parseSum(0)}
                {partialPayments.map((p, idx) => (
                    <p key={idx} style={{ margin: 0 }}>
                        {parseSum(p.amount)}
                    </p>
                ))}
            </StyledTableCell>
            {hasActions && <StyledTableCell>{parseSum(amount - partialsTotal)}</StyledTableCell>}
            <StyledTableCell>{paymentTransferDate.format('L')}</StyledTableCell>
            {hasActions && isServicer && (
                <StyledTableCell padding="checkbox" align="center">
                    <ActionsMenu
                        controlled
                        open={menuOpen}
                        handleOpen={(value = true) => setMenuOpen(value)}
                    >
                        <CreditItem onClick={handleCredit} />
                        <SettleItem
                            disabled={partialsTotal < amount && partialsTotal > 0}
                            onClick={() => setDialogOpen(true)}
                        />
                        <SendMessageItem receiver={user} />
                        <MenuSendSMS onSendSMS={onSendSMS(user)} id={id} />
                    </ActionsMenu>
                </StyledTableCell>
            )}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                PaperProps={{ sx: { minWidth: 0 } }}
            >
                <CustomDialogTitle title={texts.SETTLE_PAYMENT} />
                <DialogContent>
                    <CustomDatePicker
                        shouldDisableDate={day => day.day() === 0 || day.day() === 6}
                        onChange={handleDateChange}
                        value={settleDate}
                    />
                </DialogContent>
                <DialogActions>
                    <CancelButton
                        disabled={ongoingAction === paymentStatus.CONFIRMED}
                        onClick={() => setDialogOpen(false)}
                    >
                        {texts.CANCEL}
                    </CancelButton>
                    <ActionButton
                        disabled={ongoingAction === paymentStatus.CONFIRMED}
                        loading={ongoingAction === paymentStatus.CONFIRMED}
                        onClick={handleUpdateStatus(id, paymentStatus.CONFIRMED)}
                    >
                        {texts.SETTLE_PAYMENT}
                    </ActionButton>
                </DialogActions>
            </Dialog>
            {isConfirmed && (
                <StyledTableCell>{validatedBy ? parseFullName(validatedBy) : '-'}</StyledTableCell>
            )}
        </StyledTableRow>
    );
};

TransferRow.propTypes = {
    transfer: PropTypes.object.isRequired,
    hasActions: PropTypes.bool,
    isServicer: PropTypes.bool,
    onSendSMS: PropTypes.func,
    onCredit: PropTypes.func,
};

export default TransferRow;
